import type { AvatarCatalog, AvatarShape } from '@/shared/features/user-avatar';

import { assetsUrl } from '../../../consts/env';

import type { AvatarDecorateValue } from './schema';

/**
 * 코어 `DecorateAvatarModal` 의 파츠 선택 로직을 quiz 전용으로 포팅.
 * (avatarShapeVariants.ts + avatarAccessoryTabs.ts + AvatarContainer 의 매핑)
 */

export type CategoryTab = 'face' | 'skin' | 'hair' | 'clothes' | 'glasses';

type BaseField = 'skinId' | 'faceId' | 'hairId' | 'clothesId';

interface CategoryConfig {
  tab: CategoryTab;
  /** 카탈로그 catalog.shapes 의 키 */
  shapeKey: string;
  /** 일반 파츠면 avatar 의 필드명, 액세서리면 accessorySlot */
  field?: BaseField;
  accessorySlot?: string;
}

/** 코어 AvatarCategoryTabs 순서와 동일 (NFT 제외). 액세서리는 glasses 만 노출. */
export const CATEGORY_CONFIG: readonly CategoryConfig[] = [
  { tab: 'face', shapeKey: 'face', field: 'faceId' },
  { tab: 'skin', shapeKey: 'skin', field: 'skinId' },
  { tab: 'hair', shapeKey: 'hair', field: 'hairId' },
  { tab: 'clothes', shapeKey: 'clothes', field: 'clothesId' },
  { tab: 'glasses', shapeKey: 'glasses', accessorySlot: 'glasses' },
];

export function getCategoryConfig(tab: CategoryTab): CategoryConfig {
  return CATEGORY_CONFIG.find(c => c.tab === tab) ?? CATEGORY_CONFIG[0];
}

/** 현재 탭에서 avatar 값으로부터 선택된 partId 를 읽는다. */
export function getSelectedPartId(
  avatar: AvatarDecorateValue,
  tab: CategoryTab,
): number {
  const cfg = getCategoryConfig(tab);
  if (cfg.accessorySlot) {
    return Number(avatar.accessoryIds?.[cfg.accessorySlot] ?? 0);
  }
  return Number(avatar[cfg.field as BaseField] || 0);
}

/** 일반 파츠 선택 결과를 avatar 에 반영한 새 객체 반환. */
export function applyPartSelection(
  avatar: AvatarDecorateValue,
  tab: CategoryTab,
  partId: number,
): AvatarDecorateValue {
  const cfg = getCategoryConfig(tab);
  if (cfg.accessorySlot) {
    return {
      ...avatar,
      accessoryIds: setAccessoryId(
        avatar.accessoryIds,
        cfg.accessorySlot,
        partId,
      ),
    };
  }
  return { ...avatar, [cfg.field as BaseField]: partId };
}

// --- accessory helpers (avatarAccessoryTabs.ts 포팅) ---

export function setAccessoryId(
  accessoryIds: Record<string, number> | null | undefined,
  slot: string,
  id: number,
): Record<string, number> {
  return {
    ...Object.fromEntries(
      Object.entries(accessoryIds ?? {}).filter(
        ([, v]) => typeof v === 'number' && Number.isFinite(v),
      ),
    ),
    [slot]: id,
  };
}

/** 같은 액세서리를 다시 고르면 해제(0), 아니면 교체. */
export function getNextAccessoryId(currentId: number, selectedId: number) {
  return Math.abs(currentId) === Math.abs(selectedId) ? 0 : selectedId;
}

// --- shape variant helpers (avatarShapeVariants.ts 포팅) ---

export function shouldShowColorPicker(
  selectedShape: AvatarShape | null,
): boolean {
  return selectedShape !== null && selectedShape.parts.length > 1;
}

export function getVariantPartIdForShape(
  shape: AvatarShape,
  selectedColorIndex: number,
): { partId: number; colorIndex: number } | null {
  if (shape.parts.length === 0) return null;
  const colorIndex = Math.min(
    Math.max(selectedColorIndex, 0),
    shape.parts.length - 1,
  );
  const part = shape.parts[colorIndex];
  if (!part) return null;
  return { partId: -part.partId, colorIndex };
}

/** 코어 @zep/apis getPartHex 1:1 포팅 — 카테고리에서 partId 의 색(hex) 반환. */
export function getPartHex(
  catalog: AvatarCatalog,
  categoryKey: string,
  partId: number,
): string | null {
  const shapes = catalog.shapes[categoryKey] ?? [];
  for (const shape of shapes) {
    const part = shape.parts.find(p => p.partId === Math.abs(partId));
    if (part?.hex) return part.hex;
  }
  return null;
}

export function findShapeByPartId(
  shapes: AvatarShape[],
  partId: number,
): { shape: AvatarShape; colorIndex: number } | null {
  const absId = Math.abs(partId);
  for (const shape of shapes) {
    const idx = shape.parts.findIndex(p => p.partId === absId);
    if (idx !== -1) return { shape, colorIndex: idx };
  }
  return null;
}

/** 그리드 썸네일 아이템 (코어 selectedCategoryItems 와 동일 형태). */
export interface PartGridItem {
  id: number;
  shapeId: number;
  pathName: string;
  isNew: boolean;
  isHot: boolean;
}

export function buildGridItems(
  shapes: AvatarShape[],
  shapeKey: string,
): PartGridItem[] {
  return shapes.map(shape => {
    const previewPart = shape.parts.find(p => p.partId === shape.previewPartId);
    const versionSuffix = previewPart?.imageVersion
      ? `?v=${previewPart.imageVersion}`
      : '';
    return {
      id: -shape.previewPartId,
      shapeId: shape.id,
      pathName: `${assetsUrl}/static/assets/sprites/${shapeKey}/-${shape.previewPartId}_preview.png${versionSuffix}`,
      isNew: shape.isNew,
      isHot: shape.isHot,
    };
  });
}

/** 랜덤 아바타: 카테고리별 전체 partId 중 무작위(음수 컨벤션). 코어 pickRandom 과 동일. */
export function pickRandomPartId(shapes: AvatarShape[]): number {
  const partIds = shapes.flatMap(s => s.parts.map(p => p.partId));
  if (partIds.length === 0) return -1;
  return -partIds[Math.floor(Math.random() * partIds.length)];
}
